/**
 * Entrada: swipe en el movil, teclado en el escritorio.
 *
 * Ademas de mover al jugador, este modulo GRABA lo que hizo con su hora. El
 * servidor usa ese registro para el antifraude: con el seed puede repetir la
 * partida y comprobar que los movimientos declarados llevan de verdad a la
 * distancia y las monedas que dice el cliente. Por eso el registro se guarda
 * aqui y no en la capa de red: tiene que ser lo que paso, no lo que el juego
 * crea que paso.
 */

export const enum Accion {
  Izquierda = 0,
  Derecha = 1,
  Salto = 2,
  Deslizar = 3,
}

/** Una entrada del registro: [ms desde el inicio, accion]. */
export type EntradaRegistro = [number, Accion];

/**
 * Distancia minima en pixeles para que un arrastre cuente como swipe. Por
 * debajo de esto son toques temblorosos, y tomarlos por swipes hace que el
 * juego se sienta fuera de control.
 *
 * Bajo de 26 a 18: el gesto ahora se resuelve MIENTRAS el dedo se mueve, asi
 * que cuanto antes se reconozca, antes responde el juego.
 */
const MINIMO_SWIPE = 18;

/**
 * Un toque que no se mueve mas de esto y dura menos de MAXIMO_MS_TOQUE cuenta
 * como toque, y un toque es un salto.
 */
const MAXIMO_MS_TOQUE = 400;

/**
 * Cuanto tiene que dominar el eje horizontal para que el gesto sea un cambio de
 * carril. Por debajo de 1 el horizontal va con ventaja, a proposito: cambiar de
 * carril es la unica forma de librar un tiburon, mientras que un salto o un
 * deslizamiento muchas veces se pueden sustituir por un cambio de carril. Si
 * hay que equivocarse, mejor equivocarse hacia el lado.
 */
const DOMINA_HORIZONTAL = 0.75;

/**
 * Que accion significa un arrastre de (dx, dy) pixeles. `null` si todavia no se
 * ha movido lo bastante para decidir.
 *
 * Esta fuera de la clase y exportada para poder probarla sin navegador. Los
 * fallos de entrada que reporto el dueno el 1-oct-2026 ("a veces le doy para un
 * lado y no responde") eran invisibles para toda la suite, porque ningun test
 * tocaba la pantalla. Ahora hay uno.
 *
 * Nota de ejes: en una pantalla, la Y crece HACIA ABAJO. Por eso dy > 0 es
 * deslizarse y dy < 0 es saltar, que leido deprisa parece al reves.
 */
export function direccionDeGesto(dx: number, dy: number): Accion | null {
  const adx = Math.abs(dx);
  const ady = Math.abs(dy);
  if (Math.max(adx, ady) < MINIMO_SWIPE) return null;
  if (adx >= ady * DOMINA_HORIZONTAL) {
    return dx > 0 ? Accion.Derecha : Accion.Izquierda;
  }
  return dy > 0 ? Accion.Deslizar : Accion.Salto;
}

export class Entrada {
  private inicioX = 0;
  private inicioY = 0;
  private inicioMs = 0;
  private tocando = false;
  /** Si este gesto ya disparo su accion. Un gesto, una accion. */
  private gestoYaDisparo = false;
  private t0 = 0;

  /** El registro de la partida. Se vacia en cada `reiniciar()`. */
  private registro: EntradaRegistro[] = [];

  /**
   * Tope del registro. Una partida de 90 s con un jugador muy rapido no pasa de
   * unos cientos de entradas; 4.000 es techo de sobra. Existe para que un
   * cliente manipulado no pueda mandar un registro de megabytes y tumbar al
   * servidor validandolo.
   */
  private static readonly TOPE_REGISTRO = 4000;

  private manejador: ((a: Accion) => void) | null = null;
  private primerToque: (() => void) | null = null;
  private yaHuboToque = false;

  constructor(private readonly lienzo: HTMLElement) {}

  /** Empieza a escuchar. `alPrimerToque` sirve para despertar el audio. */
  conectar(alActuar: (a: Accion) => void, alPrimerToque?: () => void): void {
    this.manejador = alActuar;
    this.primerToque = alPrimerToque ?? null;

    // El gesto EMPIEZA en el lienzo, pero termina donde sea.
    //
    // Los eventos de fin van en `window` y no en el lienzo a proposito: si el
    // dedo sale del lienzo antes de levantarse (cosa que pasa constantemente al
    // deslizar hacia un lado cerca del borde de la pantalla), el `touchend` no
    // llega nunca al lienzo y el gesto se pierde entero. Era una de las razones
    // de que el juego "no respondiera" a veces.
    //
    // passive: false porque hay que llamar a preventDefault para que el swipe
    // no arrastre la Mini App dentro de Telegram ni haga scroll de la pagina.
    this.lienzo.addEventListener('touchstart', this.onTouchStart, { passive: false });
    window.addEventListener('touchmove', this.onTouchMove, { passive: false });
    window.addEventListener('touchend', this.onTouchEnd, { passive: false });
    window.addEventListener('touchcancel', this.onTouchEnd, { passive: false });
    this.lienzo.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('keydown', this.onKeyDown);
  }

  desconectar(): void {
    this.lienzo.removeEventListener('touchstart', this.onTouchStart);
    window.removeEventListener('touchmove', this.onTouchMove);
    window.removeEventListener('touchend', this.onTouchEnd);
    window.removeEventListener('touchcancel', this.onTouchEnd);
    this.lienzo.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('keydown', this.onKeyDown);
    this.manejador = null;
  }

  /** Reinicia el registro. Se llama al empezar cada partida. */
  reiniciar(ahoraMs: number): void {
    this.registro = [];
    this.t0 = ahoraMs;
  }

  /** El registro de la partida, para mandarlo al servidor. */
  obtenerRegistro(): readonly EntradaRegistro[] {
    return this.registro;
  }

  /**
   * El registro en forma compacta: una cadena "ms:accion,ms:accion".
   * Pesa aproximadamente un tercio que el JSON equivalente, y el servidor lo
   * parsea igual de rapido. En una partida con 300 movimientos la diferencia
   * son 3 KB por partida, que multiplicados por miles de partidas al dia si se
   * notan en el ancho de banda del VPS.
   */
  registroCompacto(): string {
    return this.registro.map(([ms, a]) => `${ms}:${a}`).join(',');
  }

  private emitir(a: Accion): void {
    if (!this.manejador) return;
    if (this.registro.length < Entrada.TOPE_REGISTRO) {
      this.registro.push([Math.round(performance.now() - this.t0), a]);
    }
    this.manejador(a);
  }

  private avisarPrimerToque(): void {
    if (this.yaHuboToque) return;
    this.yaHuboToque = true;
    this.primerToque?.();
  }

  // --- Tactil ---

  private empezarGesto(x: number, y: number): void {
    this.avisarPrimerToque();
    this.inicioX = x;
    this.inicioY = y;
    this.inicioMs = performance.now();
    this.tocando = true;
    this.gestoYaDisparo = false;
  }

  /**
   * Se llama mientras el dedo se mueve. En cuanto el gesto pasa el minimo, se
   * dispara la accion: NO se espera a levantar el dedo.
   *
   * Esto es lo que hace que los controles se sientan inmediatos. Esperando al
   * `touchend` el juego responde tarde y, si el dedo se queda apoyado, no
   * responde nunca. A 30 m/s, el tiempo que tarda una persona en levantar el
   * dedo son varios metros de pista.
   */
  private moverGesto(x: number, y: number): void {
    if (!this.tocando || this.gestoYaDisparo) return;
    const accion = direccionDeGesto(x - this.inicioX, y - this.inicioY);
    if (accion === null) return;
    this.gestoYaDisparo = true;
    this.emitir(accion);
  }

  private terminarGesto(): void {
    if (!this.tocando) return;
    const duracion = performance.now() - this.inicioMs;
    this.tocando = false;
    // Si el gesto no llego a moverse, fue un toque. Y un toque es un salto:
    // es lo que intenta todo el mundo la primera vez, y castigarlo con nada se
    // siente roto. Un dedo apoyado mucho rato NO cuenta, para que apoyarse en
    // la pantalla no provoque saltos sueltos.
    if (!this.gestoYaDisparo && duracion <= MAXIMO_MS_TOQUE) {
      this.emitir(Accion.Salto);
    }
    this.gestoYaDisparo = false;
  }

  private onTouchStart = (e: TouchEvent): void => {
    const t = e.touches[0];
    if (!t) return;
    this.empezarGesto(t.clientX, t.clientY);
  };

  private onTouchMove = (e: TouchEvent): void => {
    if (!this.tocando) return;
    // Sin esto, el swipe hacia abajo cierra la Mini App en Telegram.
    e.preventDefault();
    const t = e.touches[0];
    if (!t) return;
    this.moverGesto(t.clientX, t.clientY);
  };

  private onTouchEnd = (): void => {
    this.terminarGesto();
  };

  // --- Raton (para probar en el escritorio) ---

  private onMouseDown = (e: MouseEvent): void => {
    this.empezarGesto(e.clientX, e.clientY);
  };

  private onMouseMove = (e: MouseEvent): void => {
    this.moverGesto(e.clientX, e.clientY);
  };

  private onMouseUp = (): void => {
    this.terminarGesto();
  };

  // --- Teclado ---

  private onKeyDown = (e: KeyboardEvent): void => {
    let a: Accion | null = null;
    switch (e.key) {
      case 'ArrowLeft':
      case 'a':
      case 'A':
        a = Accion.Izquierda;
        break;
      case 'ArrowRight':
      case 'd':
      case 'D':
        a = Accion.Derecha;
        break;
      case 'ArrowUp':
      case 'w':
      case 'W':
      case ' ':
        a = Accion.Salto;
        break;
      case 'ArrowDown':
      case 's':
      case 'S':
        a = Accion.Deslizar;
        break;
      default:
        return;
    }
    e.preventDefault();
    this.avisarPrimerToque();
    this.emitir(a);
  };
}
